import React, { Component } from 'react';
import {
  Header, Footer, IllustratedHeader, ColoredHeading, CompHeading, UnorderedList, DecorativeLink
} from '@massds/mayflower-react';
import UtilityNavData from './data/UtilityNav.data';
import MainNavData from './data/MainNav.data';
import HeaderSearchData from './data/HeaderSearch.data';
import FooterLinksLiveData from './data/FooterLinksLive.json';
import SocialLinksLiveData from './data/SocialLinksLive.json';
import { ListTownBreakdown, ListCountyTable, ListSpeedLength } from './data/List.data';
import rideshareDataTown from './DataViz/data/RideshareTown.json';
import rideshareDataCounty from './DataViz/data/RideshareCounty.json';
import Table from './DataViz/Table';
import OriginTripsMap from './DataViz/Map/OriginTripsMap';
import DestinationTripsMap from './DataViz/Map/DestinationTripsMap';
import OriginTripsCapMap from './DataViz/Map/OriginTripsCapMap';
import SpeedMap from './DataViz/Map/SpeedMap';
import AgeBubble from './DataViz/Bubble/AgeBubble';
import FootNote from './FootNote';
import FootNoteLink from './FootNote/FootNoteLink';
import './App.css';
import image from './media/shutterstock_139244519.jpg';

class App extends Component {
  constructor(props) {
    super(props);
    /* eslint-enable react/no-unused-state */
    this.footerProps = {
      footerLinks: FooterLinksLiveData.footerLinks,
      socialLinks: SocialLinksLiveData.socialLinks
    };
    this.headerProps = {
      utilityNav: UtilityNavData,
      headerSearch: HeaderSearchData,
      mainNav: MainNavData,
      hideHeaderSearch: true,
      hideBackTo: true,
      siteLogoDomain: { url: { domain: 'https://www.mass.gov/' } }
    };
    this.illustratedHeader = {
      bgInfo: 'rideshare placeholder image',
      bgImage: image,
      category: '2017 Data Report',
      inverted: false,
      publishState: {
        text: ''
      },
      pageHeader: {
        title: 'Rideshare in Massachusetts',
        subTitle: '',
        optionalContents: [{
          paragraph: {
            text: 'In August 2016, Governor Baker signed <a href="https://www.mass.gov/service-details/rideshare-company-laws-and-regulations">legislation related to ridesharing companies</a>, also known as transportation network companies (TNCs). Part of that legislation helped Massachusetts start collecting data about ridesharing service from TNCs. Below, you’ll find an interactive summary of the data. You can also explore how people in your city or town are using rideshare services and learn how population density, age, and other factors impact rideshare activity. Finally, you can <a href="./data/TNC_Dataset_2017_for_download.xlsx">download the source data as an Excel spreadsheet</a>.'
          }
        }],
        headerTags: null
      }
    };
  }

  render() {
    return(
      <div className="App">
        <Header {...this.headerProps} />
        <IllustratedHeader {...this.illustratedHeader}>
          <hr />
          Data Source:
          <a href="https://www.mass.gov/orgs/department-of-public-utilities"><span className="credit"> Department of Public Utilities (DPU)</span></a>
        </IllustratedHeader>
        <main id="main-content" className="main-content">
          <section className="main-content--two">
            <ColoredHeading text="Total Rides and Rides Per Person" color="blue" centered />
            <p>
              In 2017 there were approximately 64.8 million rideshare trips started in Massachusetts. Ride sharing services are just part of the transportation picture in Massachusetts. For context, there were more than 408 million public transit trips in the Commonwealth last year. These numbers, and the following breakdown, put TNC use into perspective:
            </p>
            <UnorderedList sublist={ListTownBreakdown} />

            <CompHeading title="Total Trips Started by Municipality " level={3} centered />
            <aside className="sidebar">
              This map shows the number of rideshare trips that start in each city or town. This provides an overview of usage across Massachusetts, and there’s unsurprisingly a correlation with more densely populated areas.
              <DecorativeLink text="View map with more detail" href="http://arcg.is/jjePv" />
            </aside>
            <div className="page-content"><OriginTripsMap /></div>

            <CompHeading title="Total Destination Trips by Municipality" level={3} centered />
            <aside className="sidebar">
              While similar to the map above, this displays which cities and towns are the final destination for rideshare travelers in Massachusetts. The trends shown here are similar to the ones found in the data for where trips start. Most trips are relatively local, as the average length of a rideshare journey is 4.5 miles.
            </aside>
            <div className="page-content"><DestinationTripsMap /></div>

            <CompHeading title="Total Trips Started Per Person by Municipality" level={3} centered />
            <aside className="sidebar">
              Per person rates for trips started highlight active areas where the overall volume of rides don’t tell the whole story.
              <FootNoteLink index={3} />
              {' '}
              For example, in the Cape and Islands, Nantucket sticks out with 18.1 trips started per person, which is the seventh highest mark in Massachusetts. In Western Massachusetts, Hadley and Amherst lead the way with 7.68 and 7.52 ride starts per person, respectively. There are also notable findings in areas that do have high overall ride numbers. Despite having fewer total trips, Cambridge had a higher per person rate than Boston. Cambridge had 64.49 trips started per person compared to Boston’s 56.53.
            </aside>
            <div className="page-content"><OriginTripsCapMap /></div>

            <CompHeading title="Table of Total Trips Started and Trips Started Per Person by Municipality" level={3} centered />
            <p>Click the column headings to sort the table by municipality, trips started, or trips started per person. You can also use the search bar to look for your city or town, and navigate to other pages using the “Previous” and “Next” buttons at the bottom of the table.</p>
            <Table id="Municipality" data={rideshareDataTown} headRow="TOWN" pageSize={10} pagination />

            <CompHeading title="Table of Total Trips Started and Trips Started Per Person by County" level={3} centered />
            <p>Click the column headings to sort the table by county, trips started, or trips started per person.</p>
            <UnorderedList sublist={ListCountyTable} />
            <Table id="County" data={rideshareDataCounty} headRow="COUNTY" pageSize={14} pagination={false} />

            <ColoredHeading text="Speed and Length of Trips" color="blue" />
            <UnorderedList sublist={ListSpeedLength} />
            <CompHeading title="Rideshare Speed by Municipality" level={3} centered><FootNoteLink index={4} /></CompHeading>
            <SpeedMap />

            <ColoredHeading text="Correlated Factors in Trips Per Person" color="blue" />
            <CompHeading title="Age, Population Density, and Rideshare Trips in Greater Boston" level={3} centered><FootNoteLink index={5} /></CompHeading>
            <p>
              <b>How to read this chart:</b>
              {' '}
              Each bubble on the chart represents an individual city or town. The x axis shows population density, and the y axis shows trips started per person. The color of the bubble shows the percentage of that municipality’s population between the ages of 18-24. The darker the bubble, the higher the percentage of the population in that age range. Scroll over individual bubbles to get detailed information about specific cities or towns.
            </p>
            <aside className="sidebar">
              As mentioned above, there’s a correlation between density and rideshare use. However, there’s also a strong relationship between age and rideshare activity in the Greater Boston area. For example, after controlling for density, income, and MBTA stops, a municipality that has 20% of its population between 18-24 has 9% more rides per person than a municipality with 19% of its population in the same age group. Boston, Cambridge, and Somerville have some of the largest populations between 18-24 in the region, and they have the highest trips started per person as well.
            </aside>
            <div className="page-content"><AgeBubble /></div>

          </section>
        </main>
        <FootNote />
        <Footer {...this.footerProps} />
      </div>
    );
  }
}

export default App;
